import { useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { IconButton, InputAdornment } from '@material-ui/core'
import { useDispatch } from 'react-redux'

import { THEMES_NAME } from 'config/theme.config'
import { ThemeContext } from 'App'
import { clearReduxStateAction } from 'store/ClearReduxState'

import { Icon, Signin } from 'components'
import { AuthService, UserStorageService, UserService } from 'services'
import { AlertsService } from 'services/alerts.service'

import { YupValidator } from 'services/yupValidator.service'
import { LoginSchema } from 'store/User/User.validator'
import { traceabilityTablesActions } from 'store/TraceabilityTables'
import { traceabilityChartsActions } from 'store/TraceabilityCharts'
import { alertActions } from 'store/Alert'

import { INITIAL_STATE as INITIAL_STATE_FILTER } from 'constants/traceability.constants'

import { useTranslation } from 'react-i18next'
import * as S from './styled'
import jwt from 'jwt-decode'
import { toast } from 'react-toastify'
import { theme } from 'config'
import { submitOnEnter } from 'utils/submitOnEnter'

interface UserResponse {
  success: boolean
  data: {
    lang?: string
    token?: string
    avatar?: string
    message?: string
    profile?: string
    code?: string
  }
}

interface userToken {
  id: string
  email: string
  firstName: string
  iat: number
  exp: number
}

export const INITIAL_STATE = {
  email: '',
  password: ''
}

export function Login() {
  const { setTheme, currentTheme } = useContext(ThemeContext)
  const { t, i18n } = useTranslation()
  const [validationErrorRequest, setValidationErrorRequest] = useState('')
  const [loading, setLoading] = useState(false)
  const [form, setForm] = useState({ ...INITIAL_STATE })
  const [errors, setErrors] = useState({ ...INITIAL_STATE })
  const [passwordVisible, setPasswordVisible] = useState('password')
  const [_lang, setLang] = useState('')
  const auth = new AuthService()
  const $alert = new AlertsService()
  const dispatch = useDispatch()
  const history = useHistory()

  function changePasswordView() {
    setPasswordVisible(oldValue => (oldValue === 'text' ? 'password' : 'text'))
  }

  useEffect(() => {
    UserStorageService.signOut()
    setTheme(THEMES_NAME.jbsFriboi)
    dispatch(clearReduxStateAction.clearRedux())
    dispatch(traceabilityTablesActions.setFilters({ ...INITIAL_STATE_FILTER }))
    dispatch(traceabilityChartsActions.setInitialFilters())
  }, [loading, dispatch, setTheme])

  async function handleLogin() {
    setLoading(true)
    setErrors({ ...INITIAL_STATE })
    setValidationErrorRequest('')

    const [isValid, validationError] = await new YupValidator(
      LoginSchema()
    ).validate(form)

    if (!isValid) {
      setLoading(false)
      return setErrors(validationError as typeof INITIAL_STATE)
    }

    const { success, data }: UserResponse = await auth.login({
      ...form
    })

    if (data.code === 'NOT_FOUND') {
      toast.error(t('toasts:dataNotFound'))
    } else if (success) {
      const userData = jwt(data.token) as userToken
      const lang = localStorage.getItem('i18nextLng')
      UserStorageService.signIn(JSON.stringify({ ...data, lang }))
      const { token } = JSON.parse(UserStorageService.getToken() || '{}')
      if (token) {
        const $user = new UserService()
        await $user.updateProfile(userData.id, { lang })

        const unseenAlerts = await $alert.getUnseenAlerts()
        if (Array.isArray(unseenAlerts) && unseenAlerts.length > 0) {
          dispatch(alertActions.setAlert(unseenAlerts[0])) // Dispatch alert to Redux
        } else {
          dispatch(alertActions.clearAlert()) // Clear alert in Redux
        }
      }

      history.push('/')
    } else {
      setValidationErrorRequest(i18n.t('inputErrors:invalidData'))
    }
    setLoading(false)
  }

  function shouldInvoke() {
    if (!loading) return handleLogin()
  }

  function handleInput(value: string, name: string) {
    setErrors({ ...errors, [name]: '' })
    setForm({ ...form, [name]: value })
  }

  function GetThemeMode() {
    const THEMES_NAME = {
      jbsFriboi: (
        <>
          <S.LogoFriboi />
          <S.Title>{t('authPages:ecoB2BFrigol')}</S.Title>
        </>
      ),
      standard: (
        <>
          <S.LogoEco />
          <S.Title>{t('authPages:ecoB2BEco')}</S.Title>
        </>
      )
    }

    return THEMES_NAME[theme.appTheme]
  }

  useEffect(() => {
    const lang = i18n.language
    if (lang === 'ch') {
      setLang('zh-CN')
    } else if (lang === 'es') {
      setLang('es')
    } else if (lang === 'en-US') {
      setLang('en')
    } else {
      setLang('pt-br')
    }
  }, [i18n.language])
  return (
    <Signin>
      <S.LoginContainer>
        <GetThemeMode />
        <S.FormInput
          error={Boolean(errors.email)}
          helperText={errors.email}
          iconLeft={<Icon icon={'envelope'} height={16} width={16} />}
          label={t('common:email')}
          onInput={value => handleInput(value, 'email')}
          type="email"
          value={form.email}
        />

        <S.FormInput
          error={Boolean(errors.password)}
          helperText={errors.password}
          iconLeft={<Icon icon={'input-padlock'} height={16} width={16} />}
          iconRight={
            <InputAdornment position="start">
              <IconButton onClick={changePasswordView}>
                {
                  <Icon
                    icon={
                      passwordVisible === 'text'
                        ? 'input-visible'
                        : 'input-visible-not'
                    }
                    height={16}
                    width={16}
                  />
                }
              </IconButton>
            </InputAdornment>
          }
          label={t('common:password')}
          onInput={value => handleInput(value, 'password')}
          type={passwordVisible}
          value={form.password}
          onKeyPress={submitOnEnter(shouldInvoke)}
        />

        <S.InputError>
          <S.InputErrorIcon
            icon="itens-interface-alert"
            width={12}
            opacity={validationErrorRequest}
          />
          <S.InputErrorText>{validationErrorRequest}</S.InputErrorText>
        </S.InputError>

        <S.ForgetPassword to="/esqueci-minha-senha">
          {t('authPages:forgotPassword')}
        </S.ForgetPassword>

        <S.FormButton size="large" variant="loggedOut" onClick={shouldInvoke}>
          {t('authPages:loginButton')}
        </S.FormButton>

        <S.HelpDiv>
          <S.HelpText>{t('authPages:dontHaveAccessYet')}</S.HelpText>
          <S.GoToHelpDesk
            to={{
              pathname:
                'https://garantiadeorigem.freshdesk.com/pt-BR/support/solutions/articles/151000114720-obtenha-acesso-%C3%A0-plataforma'
            }}
            target="_blank"
          >
            {t('authPages:learnHowToAccess')}
          </S.GoToHelpDesk>
        </S.HelpDiv>

        {currentTheme === THEMES_NAME.jbsFriboi && (
          <S.FooterLogo>
            <S.LogoEcoFooter />
          </S.FooterLogo>
        )}
      </S.LoginContainer>
    </Signin>
  )
}
